import { useState, type ReactNode } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
  destructive?: boolean;
  /** When set, Confirm stays disabled until the user types this text exactly, trimmed (for example the literal word "DELETE" before closing a sharing; never the sharing's own code or name, which must never be shown in this dialog). */
  confirmationText?: string;
  /** Uppercases the confirmation field as the user types, for a confirmationText that is itself upper case (for example "DELETE"). */
  uppercaseInput?: boolean;
  /**
   * Shown inside the dialog when the confirmed action fails, so the
   * message is visible instead of landing on a page-level Alert hidden
   * behind this modal. The caller owns the state (cleared before each new
   * attempt); this component never sets it itself.
   */
  error?: string | null;
}

/**
 * A generic confirmation dialog, used for both a plain yes/no confirmation
 * (remove a member) and a typed confirmation (closing a sharing requires
 * typing the literal word "DELETE"; manager decision, 2026-09-11, never
 * the sharing's own code or name). Relies on MUI Dialog's built-in focus
 * trap and focus restoration on close for keyboard accessibility.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onConfirm,
  onClose,
  destructive,
  confirmationText,
  uppercaseInput,
  error,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Clears the typed confirmation when the dialog closes, without an
  // effect: React's documented "adjusting state when a prop changes"
  // pattern (a conditional setState during render, not in an effect,
  // avoids react-hooks/set-state-in-effect and the extra render an effect
  // would cost).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) {
      setTyped("");
    }
  }

  const requiresTyping = confirmationText !== undefined;
  const canConfirm = !requiresTyping || typed.trim() === confirmationText;

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="confirm-dialog-title">
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText component="div">{description}</DialogContentText>
        {requiresTyping && (
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={`Type "${confirmationText}" to confirm`}
            value={typed}
            onChange={(event) =>
              setTyped(uppercaseInput ? event.target.value.toUpperCase() : event.target.value)
            }
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} autoFocus={!requiresTyping}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color={destructive ? "error" : "primary"}
          variant="contained"
          disabled={!canConfirm || submitting}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
